// ─── Paywall pricing experiment — pure, dependency-free core ─────────────────
//
// Single source of truth for the experiment key, the start instant, and the
// EFFECTIVE-variant gating. Imported by BOTH the Convex server helper
// (paywallExperiment.ts) and the client hook (usePaywallVariant.ts), so every
// surface — backend checkout, server-side create guard, and the frontend hard
// paywall — resolves the same variant from the same rules.
//
// This file imports nothing from the Convex runtime (only an erased type), so
// it is safe to pull into the browser bundle.

export const PAYWALL_EXP_FLAG_KEY = "exp_paywall_pricing_v1";

// Experiment start (epoch ms). Fixed literal — never Date.now() at module load.
// 2026-06-10 00:00:00 UTC. A profile is a "fresh signup" only if its createdAt
// is at or after this instant; accounts predating it always get control.
export const PAYWALL_EXP_START_MS = 1781049600000;

export type PaywallVariant = "control" | "hardpaywall99";

// Minimal profile shape the gating needs. Both the server Doc<"userProfiles">
// and the client auth profile structurally satisfy this.
export type PaywallProfile = {
  createdAt?: number;
  stripeSubscriptionId?: string;
  planStatus?: string;
  isAnonymous?: boolean;
} | null | undefined;

// Already a (real or in-flight) subscriber? Any of these means "never the hard
// paywall / $99 price" — an existing or paying customer, not a fresh free signup.
//
// An incomplete / incomplete_expired checkout attempt does NOT count: opening
// the $99 inline checkout creates a default_incomplete subscription (id +
// planStatus land on the profile via webhook) before any payment. Counting it
// would flip a treatment user to "subscribed" → control after one paywall
// impression, dissolving the hard paywall and the server create guard.
export function hasSubscription(profile: PaywallProfile): boolean {
  if (!profile) return false;
  const status = profile.planStatus;
  if (status === "incomplete" || status === "incomplete_expired") return false;
  if (profile.stripeSubscriptionId) return true;
  return status === "active" || status === "trialing";
}

// Eligible for the treatment = fresh signup (created at/after the experiment
// start) AND not already a subscriber AND not anonymous. Anyone else is control.
export function isPaywallExperimentEligible(profile: PaywallProfile): boolean {
  if (!profile) return false;
  if (profile.isAnonymous) return false;
  if (hasSubscription(profile)) return false;
  const createdAt = profile.createdAt;
  if (typeof createdAt !== "number") return false; // unknown age → pre-existing → control
  return createdAt >= PAYWALL_EXP_START_MS;
}

// Apply the safety gate to a raw flag value. Forces "control" whenever the user
// is not an eligible fresh signup. The single chokepoint every surface funnels
// through so server / client / checkout never diverge.
export function effectivePaywallVariant(
  rawFlagValue: string | null | undefined,
  profile: PaywallProfile,
): PaywallVariant {
  if (!isPaywallExperimentEligible(profile)) return "control";
  return rawFlagValue === "hardpaywall99" ? "hardpaywall99" : "control";
}
